import { lerp } from "three/src/math/MathUtils.js";
import Stats from "three/examples/jsm/libs/stats.module.js";
import * as THREE from "three";

import { PlanePool } from "@/scripts/utils/webgl/PlanePool";
import { HeroSlider } from "@/scripts/utils/webgl/HeroSlider";
import { HeaderPreview } from "@/scripts/utils/webgl/HeaderPreview";

import { initWorkListing } from "../utils/webgl/WorkListing";
import { isWebglEnabled } from "./isWebglEnabled";
import { scroll } from "../base/scroll";
import { initMouse } from "../utils/mouse";

export interface WebGLInstance {
    dispose: () => void;
    snapshot: () => Promise<Blob | null>;
    planePool: PlanePool;
    heroSlider: HeroSlider;
    headerPreview: HeaderPreview;
    renderer: THREE.WebGLRenderer;
    setFrozen: (frozen: boolean) => void;
    rebindRoot: (newRoot: HTMLElement) => void;
}

const instances = new Map<HTMLElement, WebGLInstance>();
const frustum = new THREE.Frustum();
const viewProjection = new THREE.Matrix4();

export default function initWebGL(root: HTMLElement, heroIntroDelay = 1, introGate?: Promise<void>): WebGLInstance | undefined {
    const container = root.querySelector(".webgl") as HTMLDivElement;
    if (!container || !isWebglEnabled()) return;

    if (instances.has(root)) return instances.get(root); // guard against double-init

    initMouse();

    // ─── Renderer ────────────────────────────────────────────────────────────
    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);

    // ─── Scene ───────────────────────────────────────────────────────────────
    const mainScene = new THREE.Scene();

    // ─── Cameras ─────────────────────────────────────────────────────────────
    const camera = new THREE.PerspectiveCamera(45, container.clientWidth / container.clientHeight, 0.001, 200);
    camera.position.set(0, 0, 5);

    // ─── DOM planes setup ────────────────────────────────────────────────────
    const planePool = new PlanePool(mainScene, root);
    const heroSlider = new HeroSlider(mainScene, root);
    const headerPreview = new HeaderPreview(mainScene);
    const destroyWorkListing = initWorkListing(planePool, root);

    planePool.init(0, introGate);
    if (root.dataset.barbaNamespace === "home") heroSlider.init(heroIntroDelay, introGate);

    // ─── Debug ──────────────────────────────────────────────────────────────
    const hasDebugParam = new URLSearchParams(window.location.search).has("debug");
    let stats: Stats | undefined;

    if (hasDebugParam) {
        stats = new Stats();
        document.body.appendChild(stats.dom);
        import("@/scripts/utils/webgl/debug").then(({ initDebug }) => {
            initDebug(planePool, heroSlider);
        });
    }

    // ─── Resize ──────────────────────────────────────────────────────────────
    let lastRenderState: number[] = [];
    let isFrozenBg = false;
    let containerWidth = container.clientWidth;
    let containerHeight = container.clientHeight;

    const onResize = () => {
        const w = container.clientWidth,
            h = container.clientHeight;
        containerWidth = w;
        containerHeight = h;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();

        const pixelRatio = Math.min(window.devicePixelRatio, 2);
        renderer.setPixelRatio(pixelRatio);
        renderer.setSize(w, h);
        [planePool, heroSlider, headerPreview].forEach(group => group.setUniform("uPixelRatio", pixelRatio));
        lastRenderState = [];
    };

    const onContextRestored = () => {
        lastRenderState = [];
    };

    onResize();
    window.addEventListener("resize", onResize);
    renderer.domElement.addEventListener("webglcontextrestored", onContextRestored);

    // ─── Render loop ─────────────────────────────────────────────────────────
    let currentRoot = root;
    let scrollVelocity = 0;
    let rafId: number;
    const clock = new THREE.Clock();

    const animate = (): void => {
        rafId = requestAnimationFrame(animate);

        const elapsed = clock.getElapsedTime();

        const isNavigating = document.documentElement.hasAttribute("data-navigating");
        const isFrozen = currentRoot.style.position === "fixed";

        stats?.update();

        if ((isFrozen && !isNavigating) || isFrozenBg) {
            lastRenderState = [];
            return;
        }

        const rawVelocity = isNavigating ? 0 : (scroll.lenisInstance?.velocity ?? 0) / 10;

        scrollVelocity = lerp(scrollVelocity, rawVelocity, 0.1);
        if (Math.abs(scrollVelocity) < 0.001) scrollVelocity = 0;

        planePool.syncAll(camera, containerWidth, containerHeight, elapsed, scrollVelocity);
        heroSlider.syncAll(camera, containerWidth, containerHeight, elapsed, scrollVelocity);
        headerPreview.syncAll(camera, containerWidth, containerHeight, elapsed);

        const renderState = getRenderState(mainScene, camera, renderer.domElement);
        if (!hasRenderStateChanged(renderState, lastRenderState)) return;

        lastRenderState = renderState;
        renderer.setRenderTarget(null);
        renderer.render(mainScene, camera);
    };
    animate();

    // ─── Cleanup ─────────────────────────────────────────────────────────────
    const dispose = () => {
        cancelAnimationFrame(rafId);
        window.removeEventListener("resize", onResize);
        renderer.domElement.removeEventListener("webglcontextrestored", onContextRestored);
        planePool.dispose();
        destroyWorkListing();
        heroSlider.dispose();
        headerPreview.dispose();
        renderer.dispose();
        renderer.forceContextLoss();
        instances.delete(currentRoot);
        stats?.dom.remove();
    };

    function setFrozen(frozen: boolean) {
        isFrozenBg = frozen;
    }

    const snapshot = () => {
        renderer.setRenderTarget(null);
        renderer.render(mainScene, camera);
        return new Promise<Blob | null>(resolve => renderer.domElement.toBlob(resolve));
    };

    const rebindRoot = (newRoot: HTMLElement) => {
        currentRoot = newRoot;
    };

    const instance = { dispose, snapshot, planePool, heroSlider, headerPreview, renderer, rebindRoot, setFrozen };
    instances.set(root, instance);
    return instance;
}

function getRenderState(scene: THREE.Scene, camera: THREE.PerspectiveCamera, canvas: HTMLCanvasElement) {
    const state = [canvas.width, canvas.height];

    camera.updateMatrixWorld();
    frustum.setFromProjectionMatrix(viewProjection.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse));

    scene.traverseVisible(object => {
        if (!(object instanceof THREE.Mesh)) return;

        object.updateMatrixWorld();
        if (object.frustumCulled && !frustum.intersectsObject(object)) return;

        const { uniforms } = object.material as THREE.ShaderMaterial;
        const isMirrorEnabled = uniforms.uMirrorEnabled?.value > 0.5;

        state.push(object.position.x, object.position.y, object.position.z, object.scale.x, object.scale.y);

        for (const name in uniforms) {
            const { value } = uniforms[name];

            if (name === "uTime" && !isMirrorEnabled) continue;
            if (typeof value === "number") state.push(value);
            if (value instanceof THREE.Vector2) state.push(value.x, value.y);
            if (value instanceof THREE.Texture) state.push(value.id, value.version);
        }
    });

    return state;
}

function hasRenderStateChanged(nextState: number[], previousState: number[]) {
    return nextState.length !== previousState.length || nextState.some((value, index) => value !== previousState[index]);
}

export function getWebGLInstance(root: HTMLElement) {
    return instances.get(root);
}

export function rekeyWebGLInstance(oldRoot: HTMLElement, newRoot: HTMLElement) {
    const instance = instances.get(oldRoot);
    if (!instance) return;
    instances.delete(oldRoot);
    instances.set(newRoot, instance);
    instance.rebindRoot(newRoot);
}
